import type { BasicClubInfo, ClubMembership, UserInfo } from "@/core/types";

type ClubRouteUserContext = Pick<UserInfo, "clubMemberships" | "clubs"> | null | undefined;

interface ResolvedClubRouteContext {
  clubId: number;
  clubShortName: string;
  membership?: ClubMembership;
  club: BasicClubInfo;
}

/**
 * Build a canonical, base-relative club-workspace path under /club/.
 *
 * shortName is the canonical, shareable, human-readable form. When it isn't
 * available (performance-only / prospective / non-hosted clubs surfaced in the
 * district dashboards) we fall back to the numeric `c<number>` alias, which the
 * backend resolves and ClubWorkspaceLayout canonicalizes to the shortName once it
 * can be resolved. `clubNumber` here is the club's numeric id (in production the
 * internal ClubId equals the TI club number, so either resolves identically).
 */
export function buildClubWorkspacePath(shortName: string | null | undefined, clubNumber: number, subPath = ""): string {
  const identifier = shortName && shortName.length > 0 ? shortName : `c${clubNumber}`;
  return `/club/${identifier}${subPath}`;
}

export function parseClubRouteNumericAlias(clubIdentifier?: string): number | undefined {
  if (typeof clubIdentifier !== "string") return undefined;

  const match = clubIdentifier.match(/^c(\d+)$/i);
  if (!match) return undefined;

  const clubId = Number.parseInt(match[1], 10);
  return Number.isFinite(clubId) ? clubId : undefined;
}

function matchesClubShortName(clubIdentifier: string, club: Pick<BasicClubInfo, "shortName">): boolean {
  return club.shortName.toLowerCase() === clubIdentifier.toLowerCase();
}

export function resolveClubRouteContext(
  clubIdentifier: string | undefined,
  user: ClubRouteUserContext,
): ResolvedClubRouteContext | undefined {
  if (typeof clubIdentifier !== "string" || clubIdentifier.length === 0) {
    return undefined;
  }

  const exactMembership = user?.clubMemberships?.find((entry) => matchesClubShortName(clubIdentifier, entry.club));
  const exactClub = exactMembership?.club ?? user?.clubs?.find((entry) => matchesClubShortName(clubIdentifier, entry));

  if (exactClub) {
    return {
      clubId: exactClub.id,
      clubShortName: exactClub.shortName,
      membership: exactMembership,
      club: exactClub,
    };
  }

  const numericClubId = parseClubRouteNumericAlias(clubIdentifier);
  if (typeof numericClubId !== "number") {
    return undefined;
  }

  const membership = user?.clubMemberships?.find((entry) => entry.club.id === numericClubId);
  const club = membership?.club ?? user?.clubs?.find((entry) => entry.id === numericClubId);

  if (!club) {
    return undefined;
  }

  return {
    clubId: club.id,
    clubShortName: club.shortName,
    membership,
    club,
  };
}
